import type { ComponentType } from 'preact'
import { Video, Download, Calendar, PlayCircle, Megaphone } from 'lucide-preact'

export type WebTypeKey = 'OnlineEvent' | 'Download' | 'Meeting' | 'Demo'

// Lucide icon components vary slightly in their prop typing vs. standard FC definitions under Preact.
// For simplicity in this mapping and to avoid noisy type incompatibilities, we allow `any` here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconType = ComponentType<any>

// Meta includes icon plus Tailwind utility classes for the colored chip.
// We keep the styling minimal so the calling component can still add layout/spacing classes.
type Meta = { Icon: IconType; chipClass: string }

const META: Record<WebTypeKey, Meta> = {
    OnlineEvent: {
        Icon: Video,
        chipClass: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300'
    },
    Download: {
        Icon: Download,
        chipClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300'
    },
    Meeting: {
        Icon: Calendar,
        chipClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300'
    },
    Demo: {
        Icon: PlayCircle,
        chipClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
    },
}

const DEFAULT_META: Meta = {
    Icon: Megaphone,
    chipClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
}

/**
 * Accepts either form of a web type — the enum name ("OnlineEvent") or its description
 * ("Online Event") — because matching the enum name alone meant every caller holding a description
 * fell through to the neutral default chip without anything looking wrong.
 *
 * ⚑ Both forms used to arrive off the wire: `CampaignCardsService` sent the name on campaign cards
 * while `WebMapper` sent the description on the web view DTO. That is fixed as of ORB-849 — every
 * mapper sends the description now — so this leniency is no longer load bearing for the API. It
 * stays because the keys of `META` are still the enum names, which is what the lookup compares
 * against, and because callers also hand it asset-type strings ("OnlineEvent", see api/asset.ts).
 */
export function getWebTypeMeta(type: string | undefined | null): Meta {
    if (!type) return DEFAULT_META
    const match = matchKey(type)
    return match ? META[match] : DEFAULT_META
}

function matchKey(type: string): WebTypeKey | undefined {
    const normalized = type.replace(/\s+/g, '').toLowerCase()
    return (Object.keys(META) as WebTypeKey[]).find(key => key.toLowerCase() === normalized)
}

/** A web type as its enum name, from either wire form, for a caller that branches on it. Undefined if unrecognised. */
export function webTypeKey(type: string | undefined | null): WebTypeKey | undefined {
    return type ? matchKey(type) : undefined
}

const LABELS: Record<WebTypeKey, string> = {
    OnlineEvent: 'Online Event',
    Download: 'Download',
    Meeting: 'Meeting',
    Demo: 'Demo',
}

/**
 * A web type as a reader should see it, from either wire form.
 *
 * ⚑ So a screen does not read differently depending on which API version is behind it. ORB-849
 * changed `CampaignCardsService` to send the description like every other mapper, but a dashboard
 * running ahead of that API still receives the enum name — which put "OnlineEvent" in a column
 * that reads "Online Event" everywhere else. Anything unrecognised comes back as it arrived rather
 * than blank: an unknown type is still worth showing.
 */
export function webTypeLabel(type: string | undefined | null): string {
    if (!type) return ''
    const match = matchKey(type)
    return match ? LABELS[match] : type
}
